import { formatHandle, listenerLabel } from '../../lib/listenerName';
/** `GET /admin/listeners/temp-tracking` — recorded vs missed online time. */
export interface TempTrackingRow {
  id: string;
  displayName: string;
  handle: string;
  phoneLast4: string;
  status: string;
  isSuspended: boolean;
  isDummy: boolean;
  /** Regular online history (what callers could reach). */
  recordedOnlineSeconds: number;
  /** Old-build time the regular history MISSED: the app showed Online, the
   *  server held the listener OFFLINE (not callable). */
  missedOnlineSeconds: number;
  /** Recorded ∪ missed — an overlap counted once. */
  totalOnlineSeconds: number;
  missedSessions: number;
  lastMissedAt: string | null;
  /** Right now: callable, app shows Online but not callable, or neither. */
  now: 'online' | 'missing' | null;
}

export interface TempTrackingReport {
  range: { from: string; to: string; tz: string; now: string };
  /** First tracked beat ever — nothing before it can be shown. */
  trackingStartedAt: string | null;
  rows: TempTrackingRow[];
}

export interface TempTrackingSession {
  startedAt: string;
  lastSeenAt: string;
  durationSeconds: number;
  beats: number;
  lastAppState: string | null;
}

/** h:mm:ss for the CSV (spreadsheet-friendly, never "1h 5m"). */
export function hms(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function cell(v: string | number): string {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** One line per listener: the figures needed to pay out the missed time. */
export function tempTrackingCsv(report: TempTrackingReport): string {
  const header = [
    'Listener',
    'Handle',
    'Phone (last 4)',
    'Recorded online (h:mm:ss)',
    'Missed online (h:mm:ss)',
    'Total online (h:mm:ss)',
    'Recorded seconds',
    'Missed seconds',
    'Total seconds',
    'Missed sessions',
    'Last missed at',
    'Range from',
    'Range to',
  ];
  const lines = report.rows.map((r) =>
    [
      // The admin display name; an empty one falls back to the @handle.
      listenerLabel({ displayName: r.displayName, handle: r.handle, id: r.id }),
      formatHandle(r.handle) ?? '',
      r.phoneLast4,
      hms(r.recordedOnlineSeconds),
      hms(r.missedOnlineSeconds),
      hms(r.totalOnlineSeconds),
      r.recordedOnlineSeconds,
      r.missedOnlineSeconds,
      r.totalOnlineSeconds,
      r.missedSessions,
      r.lastMissedAt ?? '',
      report.range.from,
      report.range.to,
    ]
      .map(cell)
      .join(','),
  );
  return [header.map(cell).join(','), ...lines].join('\n');
}
